import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  WritableSignal,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, filter, take, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { CustomEventModel } from '@models/custom-event.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { UiButtonComponent } from '@ui';
import { DatasetTableConfig, UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { UtilsService } from '../../../../../../edge/utils.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import {
  CreateEventTagFieldCellContext,
  createEventTagResultTableConfig,
} from './create-event-tag-result-table.util';

/**
 * The page's input bounds — deliberately narrower than its TS check, which
 * accepts up to 180 while the backend DTO caps the field at 60. Validating
 * against the server's ceiling means this panel cannot build a rejected request.
 */
const MIN_VIDEO_LENGTH = 10;
const MAX_VIDEO_LENGTH = 60;
const MAX_FIELDS = 10;

/**
 * Cmd+K editor for one event tag, opened as an overlay over the event tags list.
 * It mirrors Database → Event tags → Create/Edit, but owns its own save: the
 * page's save is driven by the Database shell's header button through the global
 * `pressSave$` stream, which any mounted Database page also listens to.
 *
 * The seed's `_id` decides the mode — absent creates, present updates — the same
 * split `CustomEventsEditComponent` makes, so one form serves both.
 *
 * Both success actions echo the request object rather than the saved document,
 * so a save cannot splice a row in locally — the list is re-fetched. That echo is
 * also how a save is matched back to this panel: the success action carries the
 * very reference that was dispatched.
 */
@Component({
  selector: 'app-create-event-tag-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    FormsModule,
    MatSelectModule,
    UiButtonComponent,
    UiDatasetTableComponent,
  ],
  templateUrl: './create-event-tag-result.component.html',
  styleUrls: ['./create-event-tag-result.component.scss'],
})
export class CreateEventTagResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly intelligenceToolsService = inject(IntelligenceToolsService);
  private readonly utilsService = inject(UtilsService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);

  protected readonly customEventFieldType: typeof CustomEventModel.CustomEventFieldType =
    CustomEventModel.CustomEventFieldType;
  protected readonly minVideoLength: number = MIN_VIDEO_LENGTH;
  protected readonly maxVideoLength: number = MAX_VIDEO_LENGTH;
  protected readonly maxFields: number = MAX_FIELDS;

  protected readonly saving: WritableSignal<boolean> = signal<boolean>(false);

  private readonly nameCellTemplate: Signal<TemplateRef<CreateEventTagFieldCellContext> | undefined> =
    viewChild<TemplateRef<CreateEventTagFieldCellContext>>('nameCell');
  private readonly typeCellTemplate: Signal<TemplateRef<CreateEventTagFieldCellContext> | undefined> =
    viewChild<TemplateRef<CreateEventTagFieldCellContext>>('typeCell');

  protected readonly tableConfig: Signal<DatasetTableConfig<CustomEventModel.CustomEventField> | null> =
    computed(() => {
      const nameCellTemplate = this.nameCellTemplate();
      const typeCellTemplate = this.typeCellTemplate();

      if (!nameCellTemplate || !typeCellTemplate) {
        return null;
      }

      return createEventTagResultTableConfig({ nameCellTemplate, typeCellTemplate });
    });

  /** Overlay payload set by event-tags-result; null once another result is shown. */
  private readonly draft: Signal<IntelligenceModels.EventTags.CreateDraft | null> = computed(() => {
    const result = this.resultsService.current();
    return result?.type === IntelligenceModels.EventTags.PanelResultType.CreateEventTag
      ? (result.data as IntelligenceModels.EventTags.CreateDraft)
      : null;
  });

  /**
   * Read once, then mutated in place through `ngModel` — the same shape the page
   * edits, so the two surfaces stay literally the same form.
   */
  protected readonly customEvent: CustomEventModel.CustomEvent;

  public constructor() {
    this.customEvent = this.draft()?.customEvent ?? {
      name: '',
      videoLength: MIN_VIDEO_LENGTH,
      fields: [{ name: '', type: undefined }],
    };

    this.resultsService.setHeaderActions([
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_DEFAULT,
        label: 'COMMON.CANCEL',
        tooltip: 'COMMON.CANCEL',
        onClick: () => this.cancel(),
        disabled: () => this.saving(),
      },
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_PRIMARY,
        label: 'DATABASE.BUTTONS.SAVE_TAG',
        tooltip: 'DATABASE.BUTTONS.SAVE_TAG',
        onClick: () => this.save(),
        loading: () => this.saving(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected addField(): void {
    if (this.customEvent.fields.length >= MAX_FIELDS) return;
    this.customEvent.fields = [...this.customEvent.fields, { name: '', type: undefined }];
  }

  protected onRowAction(event: IntelligenceModels.EventTags.FieldRowActionEvent): void {
    if (event.actionId === IntelligenceModels.EventTags.FieldRowActionId.Delete) {
      this.removeField(event.row);
    }
  }

  private removeField(field: CustomEventModel.CustomEventField): void {
    const index = this.customEvent.fields.indexOf(field);
    if (index === -1) return;

    const fields = [...this.customEvent.fields];
    fields.splice(index, 1);
    this.customEvent.fields = fields;
  }

  private cancel(): void {
    this.resultsService.clearOverlay();
  }

  private save(): void {
    if (this.saving()) return;

    if (!this.customEvent.name) {
      this.customEvent.name = this.translateService.instant('EDIT_CUSTOM_EVENT.PLACEHOLDERS.UNTITLED');
    }
    if (!this.validate()) return;

    // The effect echoes this exact reference back on success, so an identity
    // check tells our save apart from one made by a Database page behind us.
    const customEvent = _.cloneDeep(this.customEvent);
    // The seed's `_id` is what tells editing from creating — the same split the
    // Database editor makes, so one form serves both here too.
    const editing = !!customEvent._id;
    const saved$: Observable<{ customEvent: CustomEventModel.CustomEvent }> = this.actions$.pipe(
      ofType(
        editing ? CustomEventsActions.editCustomEventSuccess : CustomEventsActions.addCustomEventSuccess,
      ),
      filter((action) => action.customEvent === customEvent),
      take(1),
    );

    // Neither effect has a failure action — both only surface the server error
    // as a toast — so a toast arriving first releases the button for a retry.
    this.actions$
      .pipe(
        ofType(SharedActions.showMessage),
        takeUntil(saved$),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.saving.set(false));

    saved$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.returnToList());

    this.saving.set(true);
    this.store$.dispatch(
      editing
        ? CustomEventsActions.editCustomEvent({ customEvent })
        : CustomEventsActions.addCustomEvent({ customEvent }),
    );
  }

  /** The page's rules, without its side-effecting `isValid` getter. */
  private validate(): boolean {
    if (
      this.customEvent.videoLength < MIN_VIDEO_LENGTH ||
      this.customEvent.videoLength > MAX_VIDEO_LENGTH
    ) {
      this.store$.dispatch(
        SharedActions.showMessage({ error: this.translateService.instant('TOASTS.VIDEO_LENGTH') }),
      );
      return false;
    }

    for (const field of this.customEvent.fields) {
      // `Text` is 0, so an unpicked type has to be told apart from a picked one.
      if (!field.name || (!field.type && field.type !== CustomEventModel.CustomEventFieldType.Text)) {
        this.store$.dispatch(
          SharedActions.showMessage({
            error: this.translateService.instant('TOASTS.ALL_FIELDS_NAME_TYPE'),
          }),
        );
        return false;
      }
    }

    return true;
  }

  /** After a successful save, swap the panel back to a refreshed event tags list. */
  private async returnToList(): Promise<void> {
    try {
      const response = await this.intelligenceToolsService.fetchEventTags();
      this.resultsService.replaceCurrentWithEventTags(response);
    } catch (err) {
      this.resultsService.clearOverlay();
      this.store$.dispatch(
        SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }),
      );
    } finally {
      this.saving.set(false);
    }
  }
}
